import { useMemo } from 'react';

import { useSafeIsFocused } from '@sg/garnish';

import { useContentfulContentTypeEntries } from '@order/Contentful';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContent = () => {
  const isScreenFocused = useSafeIsFocused();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data, fetching: isFetchingMenuContent } = useContentfulContentTypeEntries<Content>({
    contentType: CONTENT_TYPE,
    pause: !isScreenFocused,
  });

  const fields = data?.[0]?.fields ?? FALLBACK_CONTENT;

  // ─── Derived Data ────────────────────────────────────────────────────

  const mappedContent = useMemo<MappedContent>(() => {
    const categoriesBadges = fields.categoriesBadges;

    return {
      categoriesBadges: Object.fromEntries(
        categoriesBadges.filter(checkIfValidCategoryBadge).map(parseCategoryBadge),
      ),
      caloriesOverrides: parseCaloriesOverrides(fields?.caloriesOverrides as unknown),
    };
  }, [fields]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    mappedContent,
    isFetchingMenuContent,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfValidCategoryBadge(entry: string) {
  return CATEGORY_BADGE_PATTERN.test(entry);
}

function parseCategoryBadge(entry: string): [string, string] {
  const [id, badge] = entry.split('-');

  return [id, badge];
}

function parseCaloriesOverrides(rawEntries: unknown): CaloriesOverrides {
  if (!rawEntries || typeof rawEntries !== 'object') return {};

  const entries = Object.entries(rawEntries);
  const parsedEntries = entries.map(parseCaloriesOverrideEntry);
  const filteredEntries = parsedEntries.filter(checkIfValidCaloriesOverrideEntry);

  return Object.fromEntries(filteredEntries);
}

function parseCaloriesOverrideEntry([slug, rawEntry]: [unknown, unknown]):
  | CaloriesOverrideEntry
  | undefined {
  const hasInvalidEntry = typeof slug !== 'string' || !rawEntry || typeof rawEntry !== 'object';

  if (hasInvalidEntry) return;

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const record = rawEntry as Record<string, unknown>;

  const override = record.caloriesOverride;
  const overrideText = record.caloriesOverrideText;

  const hasValidOverride = typeof override === 'number' && !Number.isNaN(override);
  const hasValidOverrideText = typeof overrideText === 'string';

  const caloriesOverride = hasValidOverride ? override : undefined;
  const caloriesOverrideText = hasValidOverrideText ? overrideText : undefined;

  if (caloriesOverride === undefined && caloriesOverrideText === undefined) {
    return;
  }

  const parsedEntry: CaloriesOverrideData = { caloriesOverride, caloriesOverrideText };

  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- narrowed by hasInvalidEntry guard
  return [slug, parsedEntry];
}

function checkIfValidCaloriesOverrideEntry(
  entry: CaloriesOverrideEntry | undefined,
): entry is CaloriesOverrideEntry {
  return Boolean(entry);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPE = 'orderMenuScreenContent';
const CATEGORY_BADGE_PATTERN = /^\d+-[A-Za-z0-9 ]+$/; // mirrors Contentful rule. just an extra security layer.

const FALLBACK_CONTENT: Content = {
  categoriesBadges: [],
  caloriesOverrides: {},
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Content = {
  categoriesBadges: string[];
  caloriesOverrides?: CaloriesOverrides;
};

type MappedContent = {
  categoriesBadges: Record<string, string>;
  caloriesOverrides: CaloriesOverrides;
};

type CaloriesOverrideEntry = [string, CaloriesOverrideData];

type CaloriesOverrideData = {
  caloriesOverride?: number;
  caloriesOverrideText?: string;
};

type CaloriesOverrides = Record<string, CaloriesOverrideData>;
